import { AgGridReact } from "ag-grid-react";
import { type RefObject, useCallback } from "react";
import {
  ColumnMenuModule,
  ExcelExportModule,
  ColumnsToolPanelModule
} from "ag-grid-enterprise";
import {
  type ColDef,
  ModuleRegistry,
  CsvExportModule,
  type ColGroupDef,
  ValidationModule,
  TextFilterModule,
  DateFilterModule,
  NumberFilterModule,
  AllCommunityModule,
  type GridReadyEvent,
  type CellClickedEvent,
  ClientSideRowModelModule,
  type PaginationChangedEvent
} from "ag-grid-community";

interface FormSubmissionTableProps<T extends object> {
  height?: string;
  pageSize?: number;
  rowData: T[] | null;
  gridRef?: RefObject<AgGridReact<T>>;
  columnDefs: (ColDef<T> | ColGroupDef<T>)[] | null;
  onSortChanged?: () => void;
  onFilterChanged?: () => void;
  onGridReady?: (params: GridReadyEvent) => void;
  onCellClicked?: (event: CellClickedEvent<T, unknown>) => void;
  onPaginationChanged?: (event: PaginationChangedEvent<T>) => void;
}

export const FormSubmissionTable = <T extends object>({
  rowData,
  gridRef,
  columnDefs,
  pageSize = 1,
  height = "80vh",
  onGridReady,
  onCellClicked,
  onSortChanged,
  onFilterChanged,
  onPaginationChanged
}: FormSubmissionTableProps<T>) => {
  ModuleRegistry.registerModules([
    CsvExportModule,
    TextFilterModule,
    DateFilterModule,
    TextFilterModule,
    ValidationModule,
    ColumnMenuModule,
    ExcelExportModule,
    NumberFilterModule,
    AllCommunityModule,
    NumberFilterModule,
    ColumnsToolPanelModule,
    ClientSideRowModelModule,
    ClientSideRowModelModule
  ]);

  const gridApi = gridRef?.current?.api;

  const autoSizeAllColumns = useCallback(() => {
    if (gridApi) {
      const allColumnIds: string[] = [];
      const columnDefs = gridApi.getColumnDefs();

      if (columnDefs) {
        for (const colDef of columnDefs) {
          if ("field" in colDef && colDef.field) {
            allColumnIds.push(colDef.field.toString());
          }
        }
      }

      gridApi.autoSizeColumns(allColumnIds);
    }
  }, [gridApi]);

  const handleGridReady = (params: GridReadyEvent) => {
    autoSizeAllColumns();
    if (onGridReady) {
      onGridReady(params);
    }
  };

  return (
    <div className="ag-theme-alpine" style={{ height: height, width: "100%" }}>
      <AgGridReact<T>
        ref={gridRef}
        theme="legacy"
        pagination={true}
        rowModelType="clientSide"
        paginationPageSize={pageSize}
        rowData={rowData}
        columnDefs={columnDefs}
        onGridReady={handleGridReady}
        onCellClicked={onCellClicked}
        onFilterChanged={onFilterChanged}
        onSortChanged={onSortChanged}
        paginationPageSizeSelector={[20, 50, 100, pageSize]}
        onPaginationChanged={onPaginationChanged}
        suppressTouch={true}
        sideBar={{
          toolPanels: [
            {
              id: "columns",
              labelDefault: "Columns",
              labelKey: "columns",
              iconKey: "columns",
              toolPanel: "agColumnsToolPanel"
            }
          ]
        }}
        defaultColDef={{
          editable: true,
          sortable: true,
          resizable: true,
          filter: true
        }}
        animateRows={true}
      />
    </div>
  );
};
